import { forwardRef, useCallback } from "react";
import type { GestureResponderEvent } from "react-native";
import { type ColorTokens, isWeb, styled, type TamaguiElement, View } from "tamagui";
import { Icon, type IconComponent } from "../Icon";
import { webInteractiveTransition, webPointerProps, webPressScale } from "../interactive/styles";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
const IconButtonFrame = styled(View, {
  name: "FieldIconButton",
  minWidth: "$si5.5",
  minHeight: "$si5.5",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "$brFull",
  // Pull the larger touch target back into the field's vertical rhythm so the
  // 44px hit area doesn't grow the row height.
  marginVertical: -10,
  variants: {
    disabled: {
      true: {
        cursor: "not-allowed",
        opacity: 0.5
      }
    }
  } as const
});
export type FieldIconButtonProps = {
  icon: IconComponent;
  /** Accessible label — required because the button is icon-only. */
  label: string;
  onPress: () => void;
  color?: ColorTokens;
  disabled?: boolean;
};

/**
 * Icon-only button rendered inside a field's trailing slot. Stops the press from
 * also focusing the field frame: on web via `stopPropagation`, on native by
 * capturing the touch responder before the frame can claim it (native ignores
 * `stopPropagation`, so the capture is what actually blocks the frame's
 * `onPress`).
 */
export const FieldIconButton = forwardRef<TamaguiElement, FieldIconButtonProps>(function FieldIconButton({
  icon,
  label,
  onPress,
  color = "$iconDefault",
  disabled = false
}, forwardedRef) {
  const {
    renderTag,
    interactiveProps
  } = useInteractiveElement({
    kind: "button",
    onPress,
    disabled
  });
  const handlePress = useCallback((event: GestureResponderEvent) => {
    // stopPropagation throws on native's GestureResponderEvent; gate it to web.
    if (isWeb) {
      event.stopPropagation();
    }
    interactiveProps.onPress?.(event);
  }, [interactiveProps]);

  // Native-only: win the touch responder so the frame's onPress never fires.
  const nativeResponderCapture = isWeb ? {} : {
    onStartShouldSetResponderCapture: () => !disabled
  };
  return <IconButtonFrame ref={forwardedRef} role="button" aria-label={label} disabled={disabled} {...renderTag ? {
    render: renderTag
  } : {}} {...webPointerProps} {...disabled ? {} : webPressScale} {...webInteractiveTransition} {...nativeResponderCapture} {...interactiveProps} onPress={handlePress}>
        <Icon icon={icon} size="$iconMedium" color={color} />
      </IconButtonFrame>;
});